import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
} from '@mui/material';

const getTopicName = (question, allTopics) => {
  if (question.topic_name) return question.topic_name;
  if (question.topic && typeof question.topic === 'object' && question.topic.topic) {
    return question.topic.topic;
  }
  if (question.topic && (typeof question.topic === 'number' || typeof question.topic === 'string')) {
    const topicId = typeof question.topic === 'string' ? parseInt(question.topic, 10) : question.topic;
    const topicObj = allTopics.find((topic) => topic.id === topicId);
    return topicObj ? topicObj.topic : `Topic ${topicId}`;
  }
  return 'No Topic';
};

const getTopicId = (question) => {
  if (question.topic && typeof question.topic === 'object') return question.topic.id;
  return question.topic ?? null;
};

const getSubjectId = (question) => {
  if (question.subject && typeof question.subject === 'object') return question.subject.id;
  return question.subject ?? null;
};

const buildCurriculumKey = ({ subject, board, academicYear, topic }) =>
  [subject ?? '', board ?? '', academicYear ?? '', topic ?? ''].map(String).join('||');

const buildChapterLookup = (curriculumAssociations = []) => {
  const lookup = new Map();
  (curriculumAssociations || []).forEach((assoc) => {
    if (assoc.is_active === false || assoc.topic == null) return;
    const key = buildCurriculumKey({
      subject: assoc.subject,
      board: assoc.board,
      academicYear: assoc.academic_year,
      topic: assoc.topic,
    });
    const chapterName = assoc.parent_mapping_topic_name || (!assoc.parent_mapping ? assoc.topic_name : '');
    if (chapterName) lookup.set(key, chapterName);
  });
  return lookup;
};

const getChapterName = (question, academicYear, chapterLookup) => {
  const topicId = getTopicId(question);
  const subjectId = getSubjectId(question);
  const boardId = academicYear?.board_id;
  const academicYearId = academicYear?.id;
  if (topicId == null || subjectId == null || boardId == null || academicYearId == null) {
    return 'No Chapter';
  }
  return chapterLookup.get(buildCurriculumKey({
    subject: subjectId,
    board: boardId,
    academicYear: academicYearId,
    topic: topicId,
  })) || 'No Chapter';
};

const getSubjectName = (question) => {
  if (question.subject_name) return question.subject_name;
  if (question.subject && typeof question.subject === 'object' && question.subject.name) {
    return question.subject.name;
  }
  return 'No Subject';
};

/** True when question is used in practice mode (per-question exam_mode). */
const isPracticeQuestion = (question) => {
  const raw = question.exam_mode_name ?? question.exam_mode;
  const s = raw == null ? '' : String(raw).trim().toLowerCase();
  return s === 'practice';
};

/**
 * Basic vs non-basic for report columns. Intermediate / advanced / unknown all count as "Advanced"
 * so row totals match the sum of the four mode cells (same as a two-tier bank split).
 */
const isBasicDifficulty = (difficultyName) => {
  const s = String(difficultyName ?? '').trim().toLowerCase();
  return s.includes('basic');
};

const QuestionAnalytics = ({ questions, allTopics, filters, curriculumAssociations = [] }) => {
  const [sortField, setSortField] = useState('total');
  const [sortOrder, setSortOrder] = useState('desc');
  const chapterLookup = React.useMemo(
    () => buildChapterLookup(curriculumAssociations || []),
    [curriculumAssociations]
  );

  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortField(field);
    setSortOrder(isAsc ? 'desc' : 'asc');
  };

  const reportRows = React.useMemo(() => {
    if (!questions.length) return [];

    const reportMap = new Map();
    questions.forEach((question) => {
      const subjectName = getSubjectName(question);
      const topicName = getTopicName(question, allTopics);
      const academicYears = Array.isArray(question.academic_years) && question.academic_years.length > 0
        ? question.academic_years.filter((academicYear) => {
            const matchesBoard = !filters?.board || (
              String(academicYear.board_id) === String(filters.board) ||
              String(academicYear.board) === String(filters.board)
            );
            const matchesYear = !filters?.year || (
              String(academicYear.id) === String(filters.year) ||
              String(academicYear.academic_year) === String(filters.year) ||
              String(academicYear.year) === String(filters.year)
            );
            return matchesBoard && matchesYear;
          })
        : [{ board_name: 'No Board', year: 'No Academic Year' }];

      if (!academicYears.length) {
        return;
      }

      academicYears.forEach((academicYear) => {
        const boardName = academicYear.board_name || academicYear.board || 'No Board';
        const yearName = academicYear.year || academicYear.academic_year || 'No Academic Year';
        const chapterName = getChapterName(question, academicYear, chapterLookup);
        const key = [boardName, yearName, subjectName, chapterName, topicName].join('||');

        if (!reportMap.has(key)) {
          reportMap.set(key, {
            board: boardName,
            academicYear: String(yearName),
            subject: subjectName,
            chapter: chapterName,
            topic: topicName,
            basicExam: 0,
            basicPractice: 0,
            advancedExam: 0,
            advancedPractice: 0,
            total: 0,
          });
        }

        const row = reportMap.get(key);
        const practice = isPracticeQuestion(question);
        const basic = isBasicDifficulty(academicYear.difficulty);
        if (basic) {
          if (practice) row.basicPractice += 1;
          else row.basicExam += 1;
        } else {
          if (practice) row.advancedPractice += 1;
          else row.advancedExam += 1;
        }
        row.total += 1;
      });
    });

    return Array.from(reportMap.values());
  }, [questions, allTopics, filters, chapterLookup]);

  const sortedReportRows = React.useMemo(() => {
    const sorted = [...reportRows];
    sorted.sort((a, b) => {
      const numericFields = new Set([
        'total',
        'basicExam',
        'basicPractice',
        'advancedExam',
        'advancedPractice',
      ]);
      if (numericFields.has(sortField)) {
        return sortOrder === 'asc'
          ? a[sortField] - b[sortField]
          : b[sortField] - a[sortField];
      }

      const aVal = String(a[sortField] || '').toLowerCase();
      const bVal = String(b[sortField] || '').toLowerCase();
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    return sorted;
  }, [reportRows, sortField, sortOrder]);

  const dimensionColumns = [
    { field: 'board', label: 'Board' },
    { field: 'academicYear', label: 'Academic Year' },
    { field: 'subject', label: 'Subject' },
    { field: 'chapter', label: 'Chapter' },
    { field: 'topic', label: 'Topic' },
  ];

  const usedInSortFields = [
    { field: 'basicExam', label: 'Exam' },
    { field: 'basicPractice', label: 'Practice' },
    { field: 'advancedExam', label: 'Exam' },
    { field: 'advancedPractice', label: 'Practice' },
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Report
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, maxWidth: 800 }}>
        Counts by board–year difficulty (Basic / Advanced*), then used in (Exam / Practice). Advanced* includes
        Advanced, Intermediate, and any non-Basic difficulty so the four cells sum to Total.
      </Typography>
      {sortedReportRows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1">No report data available.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                {dimensionColumns.map((column) => (
                  <TableCell key={column.field} rowSpan={2} sx={{ fontWeight: 600, verticalAlign: 'bottom' }}>
                    <TableSortLabel
                      active={sortField === column.field}
                      direction={sortField === column.field ? sortOrder : 'asc'}
                      onClick={() => handleSort(column.field)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="center" colSpan={2} sx={{ fontWeight: 600, borderBottom: 'none' }}>
                  Basic
                </TableCell>
                <TableCell align="center" colSpan={2} sx={{ fontWeight: 600, borderBottom: 'none' }}>
                  Advanced*
                </TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 600, verticalAlign: 'bottom' }}>
                  <TableSortLabel
                    active={sortField === 'total'}
                    direction={sortField === 'total' ? sortOrder : 'asc'}
                    onClick={() => handleSort('total')}
                  >
                    Total
                  </TableSortLabel>
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                {usedInSortFields.map((col) => (
                  <TableCell key={col.field} align="center" sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortField === col.field}
                      direction={sortField === col.field ? sortOrder : 'asc'}
                      onClick={() => handleSort(col.field)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedReportRows.map((row, index) => (
                <TableRow key={`${row.board}-${row.academicYear}-${row.subject}-${row.chapter}-${row.topic}-${index}`} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{row.board}</TableCell>
                  <TableCell>{row.academicYear}</TableCell>
                  <TableCell>{row.subject}</TableCell>
                  <TableCell>{row.chapter}</TableCell>
                  <TableCell>{row.topic}</TableCell>
                  <TableCell align="center">{row.basicExam}</TableCell>
                  <TableCell align="center">{row.basicPractice}</TableCell>
                  <TableCell align="center">{row.advancedExam}</TableCell>
                  <TableCell align="center">{row.advancedPractice}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {row.total}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default QuestionAnalytics;
